import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Tag, FolderTree, X, ChevronRight, Play, Clock, Settings, Eye } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { id: 'wellness', name: 'Wellness Enablement', icon: '🧠', color: '#10B981' },
  { id: 'skill', name: 'Skill Enablement', icon: '⚙️', color: '#3B82F6' },
  { id: 'recovery', name: 'Recovery & Balance', icon: '🔄', color: '#8B5CF6' }
];

const ProgramManagement = ({ user }) => {
  const navigate = useNavigate();
  const [activePillar, setActivePillar] = useState('wellness');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Form data
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [tagForm, setTagForm] = useState({ name: '', color: '#7C3AED' });
  const [programForm, setProgramForm] = useState({
    title: '', description: '', category_id: '', tags: [], status: 'draft'
  });
  const [editingProgram, setEditingProgram] = useState(null);

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const canCreatePrograms = isAdmin || user?.role === 'educator';

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [categoriesRes, tagsRes, programsRes] = await Promise.all([
        axios.get(`${API_URL}/programs/categories?pillar=${activePillar}`, { headers }),
        axios.get(`${API_URL}/programs/tags?pillar=${activePillar}`, { headers }),
        axios.get(`${API_URL}/programs?pillar=${activePillar}`, { headers })
      ]);

      setCategories(categoriesRes.data);
      setTags(tagsRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [activePillar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Seed default tags
  const seedDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/programs/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default tags created');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  // Category handlers
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/programs/categories`, {
        ...categoryForm,
        pillar: activePillar
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category created');
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/programs/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Tag handlers
  const handleCreateTag = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/programs/tags`, {
        ...tagForm,
        pillar: activePillar
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tag created');
      setShowTagModal(false);
      setTagForm({ name: '', color: '#7C3AED' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Delete this tag?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/programs/tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tag deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };

  // Program handlers
  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = { ...programForm, pillar: activePillar };
      
      if (editingProgram) {
        await axios.put(`${API_URL}/programs/${editingProgram.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Program updated');
      } else {
        await axios.post(`${API_URL}/programs`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Program created');
      }
      
      setShowProgramModal(false);
      setProgramForm({ title: '', description: '', category_id: '', tags: [], status: 'draft' });
      setEditingProgram(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save program');
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setProgramForm({
      title: program.title,
      description: program.description,
      category_id: program.category_id,
      tags: program.tags || [],
      status: program.status
    });
    setShowProgramModal(true);
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Delete this program and all its sessions?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/programs/${programId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Program deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  const toggleTag = (tagId) => {
    setProgramForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId) 
        ? prev.tags.filter(t => t !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  const currentPillar = PILLARS.find(p => p.id === activePillar);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading programs...</p>
      </div>
    );
  }

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Programs & Sessions
          </h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Manage learning programs across different pillars
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {isAdmin && (
            <button
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: showAdminPanel ? 'var(--color-primary)' : 'var(--bg-primary)',
                color: showAdminPanel ? 'white' : 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <Settings size={16} />
              Admin Settings
            </button>
          )}
          <button
            onClick={() => { setEditingProgram(null); setProgramForm({ title: '', description: '', category_id: '', tags: [], status: 'draft' }); setShowProgramModal(true); }}
            className="gradient-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
            disabled={categories.length === 0}
          >
            <Plus size={18} />
            Create Program
          </button>
        </div>
      </div>

      {/* Pillar Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {PILLARS.map(pillar => (
          <button
            key={pillar.id}
            onClick={() => setActivePillar(pillar.id)}
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              border: activePillar === pillar.id ? `2px solid ${pillar.color}` : '2px solid var(--border-color)',
              background: activePillar === pillar.id ? `${pillar.color}15` : 'var(--bg-primary)',
              color: activePillar === pillar.id ? pillar.color : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '20px' }}>{pillar.icon}</span>
            {pillar.name}
          </button>
        ))}
      </div>

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div style={{ 
          background: 'var(--bg-secondary)', 
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '24px',
          border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Admin Settings for {currentPillar?.icon} {currentPillar?.name}
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {/* Categories Section */}
            <div style={{ background: 'var(--bg-primary)', borderRadius: '10px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FolderTree size={16} />
                  Categories
                </h4>
                <button
                  onClick={() => setShowCategoryModal(true)}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: currentPillar?.color, color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                >
                  + Add
                </button>
              </div>
              {categories.length === 0 ? (
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-tertiary)' }}>No categories yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categories.map(cat => (
                    <div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>{cat.name}</span>
                      <button onClick={() => handleDeleteCategory(cat.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tags Section */}
            <div style={{ background: 'var(--bg-primary)', borderRadius: '10px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tag size={16} />
                  Tags
                </h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {activePillar === 'wellness' && tags.length === 0 && (
                    <button onClick={seedDefaults} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>
                      Seed Defaults
                    </button>
                  )}
                  <button
                    onClick={() => setShowTagModal(true)}
                    style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: currentPillar?.color, color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                  >
                    + Add
                  </button>
                </div>
              </div>
              {tags.length === 0 ? (
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-tertiary)' }}>No tags yet</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tags.map(tag => (
                    <div key={tag.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: `${tag.color}20`, borderRadius: '20px', border: `1px solid ${tag.color}` }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: tag.color }}>{tag.name}</span>
                      <button onClick={() => handleDeleteTag(tag.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tag.color, padding: '2px', display: 'flex' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Programs Grid */}
      {categories.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
          <FolderTree size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No Categories Yet</h3>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {isAdmin ? 'Create a category first to add programs' : 'Ask an admin to create categories'}
          </p>
        </div>
      ) : programs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
          <Play size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No Programs Yet</h3>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Create your first program to get started</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
          {programs.map(program => (
            <div key={program.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Color bar */}
              <div style={{ height: '6px', background: currentPillar?.color }} />
              
              <div style={{ padding: '20px' }}>
                {/* Category & Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: currentPillar?.color, textTransform: 'uppercase' }}>
                    {program.category?.name || 'Uncategorized'}
                  </span>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: program.status === 'published' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                    color: program.status === 'published' ? '#10B981' : '#F59E0B',
                    textTransform: 'capitalize'
                  }}>
                    {program.status}
                  </span>
                </div>

                {/* Title */}
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {program.title}
                </h3>

                {/* Description */}
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  {program.description?.substring(0, 100)}{program.description?.length > 100 ? '...' : ''}
                </p>

                {/* Tags */}
                {program.tag_details?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                    {program.tag_details.map((tag, i) => (
                      <span key={i} style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${tag.color || '#7C3AED'}20`,
                        color: tag.color || '#7C3AED'
                      }}>
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Duration & Sessions Count */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    <Clock size={14} />
                    {program.duration_minutes} min
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    <Play size={14} />
                    {program.session_count || 0} sessions
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => navigate(`/dashboard/programs/${program.id}`)}
                    style={{ 
                      flex: 1,
                      padding: '10px', 
                      background: currentPillar?.color, 
                      border: 'none', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <Eye size={16} />
                    View Program
                  </button>
                  <button
                    onClick={() => handleEditProgram(program)}
                    style={{ padding: '10px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                  >
                    <Edit2 size={16} />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteProgram(program.id)}
                      style={{ padding: '10px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#EF4444' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>Create Category</h2>
            <form onSubmit={handleCreateCategory}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Name *</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCategoryModal(false)} style={{ padding: '10px 20px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="gradient-btn" style={{ padding: '10px 20px' }}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <div className="modal-overlay" onClick={() => setShowTagModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>Create Tag</h2>
            <form onSubmit={handleCreateTag}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Name *</label>
                <input
                  type="text"
                  value={tagForm.name}
                  onChange={e => setTagForm({ ...tagForm, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Color</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1'].map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setTagForm({ ...tagForm, color })}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: color,
                        border: tagForm.color === color ? '3px solid var(--text-primary)' : 'none',
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowTagModal(false)} style={{ padding: '10px 20px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="gradient-btn" style={{ padding: '10px 20px' }}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Program Modal */}
      {showProgramModal && (
        <div className="modal-overlay" onClick={() => setShowProgramModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              {editingProgram ? 'Edit Program' : 'Create Program'}
            </h2>
            <form onSubmit={handleCreateProgram}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Title *</label>
                <input
                  type="text"
                  value={programForm.title}
                  onChange={e => setProgramForm({ ...programForm, title: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Description</label>
                <textarea
                  value={programForm.description}
                  onChange={e => setProgramForm({ ...programForm, description: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Category *</label>
                <select
                  value={programForm.category_id}
                  onChange={e => setProgramForm({ ...programForm, category_id: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {tags.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Tags</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          border: programForm.tags.includes(tag.id) ? `2px solid ${tag.color}` : '1px solid var(--border-color)',
                          background: programForm.tags.includes(tag.id) ? `${tag.color}20` : 'transparent',
                          color: programForm.tags.includes(tag.id) ? tag.color : 'var(--text-secondary)',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Status</label>
                <select
                  value={programForm.status}
                  onChange={e => setProgramForm({ ...programForm, status: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowProgramModal(false)} style={{ padding: '10px 20px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="gradient-btn" style={{ padding: '10px 20px' }}>
                  {editingProgram ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramManagement;
